import { SmgElement } from '../core/smg-element';
import { html, when, type SafeHtml } from '../core/safe-html';
import { ApiError } from '../models/common';
import { auth } from '../services/auth-service';
import { navigate } from '../core/router';

const REMEMBER_KEY = 'smg-login-user';

/** Giriş ekranı — mock'un login sayfasının birebir uygulanışı. */
export class LoginView extends SmgElement {
  private username = localStorage.getItem(REMEMBER_KEY) ?? '';
  private password = '';
  private showPass = false;
  private remember = localStorage.getItem(REMEMBER_KEY) != null;
  private error = '';
  private busy = false;

  protected override onConnected(): void {
    this.onAct('click', {
      togglePass: () => {
        this.showPass = !this.showPass;
        this.render();
      },
      toggleRemember: () => {
        this.remember = !this.remember;
        this.render();
      },
      submit: () => void this.submit(),
    });
    this.onAct('input', {
      user: (el) => {
        this.username = (el as HTMLInputElement).value;
        this.error = '';
      },
      pass: (el) => {
        this.password = (el as HTMLInputElement).value;
        this.error = '';
      },
    });
    this.onAct('keydown', {
      user: (_el, ev) => this.submitOnEnter(ev),
      pass: (_el, ev) => this.submitOnEnter(ev),
    });
  }

  private submitOnEnter(ev: Event): void {
    if ((ev as KeyboardEvent).key === 'Enter') void this.submit();
  }

  private async submit(): Promise<void> {
    if (this.busy) return;
    const username = this.username.trim();
    if (!username || !this.password) {
      this.error = 'Kullanıcı adı ve parola gerekli.';
      this.render();
      return;
    }
    this.busy = true;
    this.render();
    try {
      await auth.login(username, this.password);
      if (this.remember) localStorage.setItem(REMEMBER_KEY, username);
      else localStorage.removeItem(REMEMBER_KEY);
      navigate('#/devices');
    } catch (e) {
      this.error =
        e instanceof ApiError && e.status === 401
          ? 'Kullanıcı adı veya parola hatalı.'
          : 'Sunucuya ulaşılamadı — bağlantıyı kontrol et.';
    } finally {
      this.busy = false;
      this.render();
    }
  }

  protected override template(): SafeHtml {
    const borderColor = this.error ? 'var(--vx-danger)' : 'var(--vx-border)';
    return html`
      <div class="body" style="display:flex;align-items:center;justify-content:center;padding:24px;background:var(--vx-bg);width:100%">
        <div style="width:100%;max-width:380px">
          <div style="display:flex;flex-direction:column;align-items:center;gap:13px;margin-bottom:26px">
            <span style="width:52px;height:52px;border-radius:14px;background:var(--vx-primary);display:flex;align-items:center;justify-content:center">
              <i class="ti ti-server-cog" style="font-size:28px;color:#fff"></i>
            </span>
            <div style="text-align:center">
              <div style="font:600 21px var(--vx-font-sans)">Filo yönetim paneli</div>
              <div style="font:400 13px var(--vx-font-sans);color:var(--vx-text-2);margin-top:3px">SMGBox cihaz filosu · SMG</div>
            </div>
          </div>

          <div class="card" style="padding:20px">
            ${when(this.error, () => html`
              <div style="display:flex;gap:9px;align-items:flex-start;padding:11px 12px;background:var(--vx-danger-bg);border:1px solid var(--vx-danger);border-radius:var(--vx-radius-md);margin-bottom:15px">
                <i class="ti ti-alert-circle" style="color:var(--vx-danger);font-size:17px;flex:none"></i>
                <span style="font:400 12.5px/1.5 var(--vx-font-sans);color:var(--vx-danger-text)">${this.error}</span>
              </div>`)}
            <div style="display:flex;flex-direction:column;gap:14px">
              <div>
                <label class="lbl">Kullanıcı adı</label>
                <input class="inp" data-act="user" placeholder="kullanıcı adı" value="${this.username}"
                       autocomplete="username" style="border-color:${borderColor}">
              </div>
              <div>
                <label class="lbl">Parola</label>
                <div style="position:relative">
                  <input class="inp" data-act="pass" type="${this.showPass ? 'text' : 'password'}"
                         placeholder="••••••" value="${this.password}" autocomplete="current-password"
                         style="padding-right:42px;border-color:${borderColor}">
                  <button data-act="togglePass" type="button"
                          style="position:absolute;right:6px;top:6px;width:30px;height:30px;border:none;background:none;cursor:pointer;color:var(--vx-text-3);display:flex;align-items:center;justify-content:center">
                    <i class="${this.showPass ? 'ti ti-eye-off' : 'ti ti-eye'}" style="font-size:17px"></i>
                  </button>
                </div>
              </div>
              <label style="display:flex;align-items:center;gap:9px;cursor:pointer">
                <span data-act="toggleRemember" class="checkbox ${this.remember ? 'on' : ''}"><i class="ti ti-check"></i></span>
                <span style="font:400 13px var(--vx-font-sans);color:var(--vx-text-2)">Beni hatırla</span>
              </label>
              <button class="btn pri" style="width:100%" data-act="submit" ${this.busy ? 'disabled' : ''}>
                <i class="ti ti-login-2"></i>${this.busy ? 'Giriş yapılıyor…' : 'Giriş yap'}
              </button>
            </div>
          </div>
        </div>
      </div>`;
  }

  protected override wire(): void {
    // render, input değerlerini attribute'tan basar; imleci korumak için
    // yeniden çizim yalnız durum değişimlerinde yapılır (input'ta yapılmaz).
  }
}

customElements.define('smg-login-view', LoginView);
